import { defineComponent, reactive, SetupContext, readonly, computed, ref } from '@vue/composition-api';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';              // フィルタ用共通コンポーネント
import { SendHistoryDetail, TableHeader, SendHistoryDetailUser, ColumnMenu } from "@/shared/type";
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                         // Windowサイズ
    filterDefs: TableFilterDefs;                                                  // フィルタ定義
    filters: TableFilters;                                                        // フィルタ情報
    sendHistoryDetailTableHeaders: TableHeader[];                                 // 送付履歴詳細一覧ヘッダ情報
    sendHistoryDetailItem: SendHistoryDetail[];                                   // 送付履歴詳細一覧情報
    totalCount: number;                                                           // 送付履歴詳細一覧総件数
    pageOptions: DataOptions;                                                     // ページ情報を保存する変数
    isLoading: boolean;                                                           // テーブルがロード中: true ロード中でない: false
    sendUsersListDialog: ColumnMenu;                                              // 送付ユーザー確認ダイアログ表示フラグ
    sendUsersList: SendHistoryDetailUser[];                                       // 送付ユーザー一覧情報
    isLoadingDialog: boolean;                                                     // 送付ユーザー確認ダイアログがロード中: true ロード中でない: false
    isFromDetail: boolean;                                                        // 詳細画面からの遷移フラグ
}

/**
 * 重要情報のお知らせ：送付履歴詳細一覧画面コンポーネント
 */
export default defineComponent({
    components: { CommonTableFilter, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                                 // ルータ
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                     // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                                        // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[0]);                        // 1ページあたりの行数（状態管理用、デフォルト値：50）
        const sortBy = ref<string[]>([]);                                         // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                      // ソート順（状態管理用）
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                  // テーブルのデータ取得中に表示するテキスト
        const commonRef = ref<any>(null);                                         // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        const filterRef = ref<any>(null);                                         // フィルタコンポーネント

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                           // ウィンドウサイズ
            filterDefs: {                                                         // フィルタ定義
                'freeWord': { title: 'フリーワード', types: 'FreeWord' },
                'sendDivision': { title: '送付区分', types: 'CheckBox' }
            },
            filters: {},                                                          // フィルタ情報
            sendHistoryDetailTableHeaders: [],                                    // 送付履歴詳細一覧ヘッダー
            sendHistoryDetailItem: [],                                            // 送付履歴詳細一覧情報
            totalCount: 0,                                                        // 送付履歴詳細一覧総件数
            pageOptions: {                                                        // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                                     // テーブルがロード中: true ロード中でない: false
            sendUsersListDialog: {},                                              // 送付ユーザー確認ダイアログ表示フラグ
            sendUsersList: [],                                                    // 送付ユーザー一覧情報
            isLoadingDialog: false,                                               // 送付ユーザー確認ダイアログがロード中: true ロード中でない: false
            isFromDetail: false                                                   // 詳細画面からの遷移フラグ
        });
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];            // 送付履歴詳細一覧のヘッダ色
        const tableHeight = SystemConst.TABLE_HEIGHT.IMPORTANT_INFORMATION_SEND_HISTORY_DETAIL;
        const importantInfoId = computed(() => ctx.root.$route.params.id).value;  // 選択した重要情報ID
        // 送付履歴詳細一覧ヘッダ情報
        const sendHistoryDetailDisplayTableHeaders: TableHeader[] = [
            {
                text: 'タイトル',
                value: 'title',
                width: '40%',
                class: tableHeaderClass
            },
            {
                text: '送付組織',
                value: 'sendOrganization',
                width: '20%',
                class: tableHeaderClass
            },
            {
                text: '送付区分',
                value: 'sendDivision',
                width: '25%',
                class: tableHeaderClass
            },
            {
                text: '送付日',
                value: 'sendDate',
                width: '15%',
                class: tableHeaderClass
            }
        ];

        // フィルタバリデーション
        // フリーワード
        const freeWordRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.IMPORTANT_INFORMATION_LIST_MAX_LENGTH.FREE_WORD,
                'フリーワード'
            )
        ];

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '送付履歴詳細');

            // storeからフィルター条件をすべて復元
            state.filters = ctx.root.$store.state.importantInformation.filtersSendHistoryDetail;

            // storeから前回のページ番号、1ページあたりの行数を復元
            pageNation.value = ctx.root.$store.state.importantInformation.pageNationSendHistoryDetail;
            itemsPerPage.value = ctx.root.$store.state.importantInformation.itemsPerPageSendHistoryDetail;

            // storeから前回のソートキー、ソート順を復元
            sortBy.value = ctx.root.$store.state.importantInformation.sortBySendHistoryDetail;
            sortDesc.value = ctx.root.$store.state.importantInformation.sortDescSendHistoryDetail;

            // storeから詳細画面からの遷移フラグを復元
            state.isFromDetail = ctx.root.$store.state.importantInformation.isFromDetailSendHistoryDetail;

            // フィルタ情報を初期設定
            // awaitで処理の完了を待つ
            await initFilter();

            // 初期表示ヘッダ設定
            state.sendHistoryDetailTableHeaders = sendHistoryDetailDisplayTableHeaders;

            // 前回のフィルタに対するID格納用の配列
            const filterIdList = [];
            // 前回のフィルタ条件格納用の配列
            const filterParameters = [];

            // state.filtersのプロパティ(key)で以下を繰り返し、有効なフィルタだけを抽出
            for (const key in state.filters) {
                // state.filtersのプロパティにkeyがある場合
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    // 有効なフィルタである場合
                    if (val.isEnabled) {
                        // フィルタのID(key)とフィルタ条件を保存
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元された有効なフィルタがある場合
            if (filterIdList.length !== 0) {
                // setAllFiltersで有効なフィルタをすべてstateに設定
                filterRef.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // なければ送付履歴詳細一覧情報を設定
                updateList();
            }

            // 詳細画面からの遷移フラグにfalseを設定
            // trueのままだと通常のフィルタリング時に1ページ目へ戻らなくなるため
            state.isFromDetail = false;
        }

        /**
         * フィルタ情報を取得・設定
         */
        async function initFilter() {
            // 送付履歴詳細一覧フィルタ情報APIでフィルタに表示する情報を取得
            // GET /api/important-information/send-history/detail-list/filter-conditions
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('importantInformation/getSendHistoryDetailFilter');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 以下の項目にAPIで取得したフィルタ情報を設定
            // 送付区分
            state.filterDefs['sendDivision'].chkItems = response.data.sendDivision;

            // 以下の項目にフィルタバリデーションを設定
            // フリーワード
            state.filterDefs['freeWord'].validator = freeWordRules;
        }

        /**
         * 送付履歴詳細一覧の表示更新
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // 現在のページ情報/ソート情報、フィルタ条件をストアに保存
            ctx.root.$store.commit('importantInformation/setPageNationSendHistoryDetail', pageNation.value);
            ctx.root.$store.commit('importantInformation/setItemsPerPageSendHistoryDetail', itemsPerPage.value);
            ctx.root.$store.commit('importantInformation/setFiltersSendHistoryDetail', state.filters);
            ctx.root.$store.commit('importantInformation/setSortBySendHistoryDetail', sortBy.value);
            ctx.root.$store.commit('importantInformation/setSortDescSendHistoryDetail', sortDesc.value);

            // makeListParametersでAPIパラメータを作成する
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 重要情報のお知らせ送付履歴詳細一覧取得APIでデータを取得する
            // GET /api/important-information/send-history/detail-list/{importantInfoId}
            // パラメータ：重要情報ID
            //            ページ番号
            //            1ページあたりの行数
            //            ソートキー
            //            ソート順
            //            フィルタ条件
            const response = await ctx.root.$store.dispatch('importantInformation/getSendHistoryDetailListInformation',
                {
                    importantInfoId: importantInfoId,
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                if (response.status === HttpStatus.NOT_FOUND || response.status === HttpStatus.BAD_REQUEST) {
                    // 400, 404エラーの場合、エラーメッセージを表示して送付履歴一覧画面に戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/important-information/send-history/list');
                    return;
                }
                // その他のエラーの場合、エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 送付履歴詳細一覧のデータをstate.sendHistoryDetailItemへ設定する
            state.sendHistoryDetailItem = response.data.sendHistoryDetailLists;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * ページ情報/ソート情報変更に対するイベント
         * @param options ページ情報/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ情報をstateへ保存する
            state.pageOptions = options;

            // 状態管理用変数とoptionsの中身と比較して、いずれも変更されていなかった場合
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ番号、1ページあたりの行数を設定する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソートキー、ソート順を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 送付履歴詳細一覧を更新する
            updateList();
        }

        /**
         * フィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFilters(filters: TableFilters) {
            // filtersをstateへ保存する
            state.filters = filters;

            // 詳細画面からの遷移でない場合
            if (!state.isFromDetail) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // 送付履歴一覧を更新する
            updateList();
        }

        /**
         * 送付ユーザー取得
         * @param importantInfoId 重要情報ID
         * @param sendSequence 送付シーケンス
         */
        async function getSendUsers(importantInfoId: string, sendSequence: string) {
            // state.sendUsersListを初期化
            state.sendUsersList = [];

            // 送付ユーザー確認ダイアログがロード中のフラグをtrueに設定する
            state.isLoadingDialog = true;

            // 重要情報のお知らせ送付ユーザー一覧取得APIでデータを取得する
            // GET /api/important-information/send-history/detail-list/send-users/{importantInfoId}/{sendSequence}
            // パラメータ：重要情報ID
            //            送付シーケンス
            const response = await ctx.root.$store.dispatch('importantInformation/getSendHistoryDetailSendUsers',
                {
                    importantInfoId: importantInfoId,
                    sendSequence: sendSequence
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 送付ユーザー確認ダイアログがロード中のフラグをfalseに設定する
                state.isLoadingDialog = false;

                // 送付ユーザー確認ダイアログ表示フラグをfalseにする
                state.sendUsersListDialog[sendSequence] = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 送付ユーザー一覧のデータをstate.sendUsersListへ設定する
            state.sendUsersList = response.data.sendUsers;

            // 送付ユーザー確認ダイアログがロード中のフラグをfalseに設定する
            state.isLoadingDialog = false;
        }

        /**
         * 重要情報のお知らせ詳細画面へ遷移する
         * 
         * @param importantId 重要情報ID
         * @param sequence 送付シーケンス
         */
        function navigationDetail(importantId: string, sequence: string) {
            // storeの詳細画面からの遷移フラグをtrueに設定
            ctx.root.$store.commit('importantInformation/setIsFromDetailSendHistoryDetail', true);

            router.push('/important-information/detail/' + importantId + '/' + sequence + '/send');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 戻るボタン押下処理
         */
        function back() {
            router.back();
        }

        return {
            state: readonly(state),
            tableHeight,
            linesPerPage,
            pageNation,
            itemsPerPage,
            sortBy,
            sortDesc,
            tableLoadingText,
            commonRef,
            filterRef,
            updateOptions,
            updateFilters,
            getSendUsers,
            navigationDetail,
            onResize,
            back
        };
    }
});
